import { useLazyQuery } from '@apollo/client';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Box } from '@ovComponents/1-primative/box/box';
import { Skeleton } from '@ovComponents/1-primative/skeleton/skeleton';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import {
  ButtonVariants,
  ConfirmationModal,
  ConfirmButtonColors,
} from '@ovComponents/3-pattern/confirmationDialog/confirmationDialog';
import { useGlobalToast } from '@providers/globalToastProvider';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { UserContext } from '@providers/userContextProvider';
import { dollars2cents } from '@utils/dollars2cents';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTradingMutation } from '../hooks/useTradingMutation';
import { FETCH_OPTIMIZED_PORTFOLIO_REPORTS } from '../queries';
import {
  LABEL_SX,
  MICRO_SX,
  MICRO_UPPER_SX,
  SMALL_BOLD_SX,
  SMALL_SX,
  TRUNCATE_SX,
  VALUE_SX,
} from '../styles';
import type { OptimizerState } from '../types';
import {
  buildExecutiveSummary,
  computeHoldingDiffs,
  formatMoney,
  getClientName,
  getPortfolioName,
  getStageIndex,
  getTriggers,
  hexAlpha,
  isRegistered,
  OPTIMIZER_STAGE_GROUPS,
  OPTIMIZER_STATE_LABELS,
  parseAccountType,
  type SysColors,
  tradeSideColor,
} from '../utils';
import {
  REGISTERED_TYPES,
  TERMINAL_STATES,
  TRIGGER_EXPLANATIONS,
} from '../utils/constants';
import { TRANSITION_PORTFOLIO_OPTIMIZER } from '../utils/mutations';
import { TriggerBadge } from './shared';

type NormalizedTrade = {
  id: string;
  type: string;
  account: string;
  ticker: string;
  exchange?: string;
  quantity: number;
  price: number;
  totalValue: number;
  currency?: string;
  tradeDate?: string;
  settlementDate?: string;
  isSellAll?: boolean;
  securityName?: string;
  state?: string;
};

type TaxInsight = {
  registeredSellCents: number;
  taxableSellCents: number;
  registeredBuyCents: number;
  taxableBuyCents: number;
  registeredSellPct: number;
  totalBuys: number;
  totalSells: number;
  accountBreakdown: Record<
    string,
    { buys: number; sells: number; buyCents: number; sellCents: number }
  >;
};

type OptimizerInlineWorkflowProps = {
  operation: Record<string, any>;
  onRefresh: () => void;
  trades?: NormalizedTrade[];
  loadingTrades?: boolean;
  parentPolling?: boolean;
};

const computeTaxInsight = (trades: NormalizedTrade[]): TaxInsight | null => {
  if (trades.length === 0) return null;

  let registeredSellCents = 0;
  let taxableSellCents = 0;
  let registeredBuyCents = 0;
  let taxableBuyCents = 0;
  const accountBreakdown: Record<
    string,
    { buys: number; sells: number; buyCents: number; sellCents: number }
  > = {};

  trades.forEach((t) => {
    const acctType = parseAccountType(t.account);
    const reg = isRegistered(acctType);
    const isBuy = t.type === 'BUY' || t.type?.includes('BUY');
    const cents = dollars2cents(t.totalValue);
    const key = acctType || 'Unknown';

    if (!accountBreakdown[key]) {
      accountBreakdown[key] = { buys: 0, sells: 0, buyCents: 0, sellCents: 0 };
    }
    const ab = accountBreakdown[key];

    if (isBuy) {
      ab.buys++;
      ab.buyCents += cents;
      if (reg) registeredBuyCents += cents;
      else taxableBuyCents += cents;
    } else {
      ab.sells++;
      ab.sellCents += cents;
      if (reg) registeredSellCents += cents;
      else taxableSellCents += cents;
    }
  });

  const totalSellCents = registeredSellCents + taxableSellCents;
  const registeredSellPct =
    totalSellCents > 0 ? (registeredSellCents / totalSellCents) * 100 : 0;

  return {
    registeredSellCents,
    taxableSellCents,
    registeredBuyCents,
    taxableBuyCents,
    registeredSellPct,
    accountBreakdown,
    totalBuys: trades.filter((t) => t.type === 'BUY' || t.type?.includes('BUY'))
      .length,
    totalSells: trades.filter(
      (t) => t.type === 'SELL' || t.type?.includes('SELL'),
    ).length,
  };
};

const OptimizerInlineWorkflow = ({
  operation,
  onRefresh,
  trades = [],
  loadingTrades = false,
  parentPolling = false,
}: OptimizerInlineWorkflowProps) => {
  const { sys } = useThemeTokens();
  const { activeOrganization } = useContext(UserContext);
  const { showToast } = useGlobalToast();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const state = operation.state as OptimizerState;
  const isTerminal = TERMINAL_STATES.includes(state);
  const isStale =
    !isTerminal &&
    Date.now() - new Date(operation.createdAt).getTime() > 24 * 60 * 60 * 1000;
  const currentStageIdx = getStageIndex(state);

  const [transitionOptimizer, { loading: transitioning }] = useTradingMutation(
    TRANSITION_PORTFOLIO_OPTIMIZER,
  );

  const [fetchReports, { data: reportsData, loading: loadingReports }] =
    useLazyQuery(FETCH_OPTIMIZED_PORTFOLIO_REPORTS, {
      fetchPolicy: 'cache-first',
    });

  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (parentPolling) return;
    if (!isTerminal && !isStale) {
      pollRef.current = setInterval(() => onRefreshRef.current(), 3_000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [isTerminal, isStale, parentPolling]);

  useEffect(() => {
    if (operation.id && activeOrganization?.id) {
      fetchReports({
        variables: {
          input: {
            portfolioOptimizerId: operation.id,
            organizationId: activeOrganization.id,
            pagination: { perPage: 200, offSet: 0 },
          },
        },
      });
    }
  }, [operation.id, activeOrganization?.id, fetchReports]);

  const decisionSummary = useMemo(() => {
    const reports =
      reportsData?.fetchOptimizedPortfolioReports?.portfolioReports || [];
    const totalCount =
      reportsData?.fetchOptimizedPortfolioReports?.totalCount || reports.length;
    let driftCount = 0;
    let transferCount = 0;
    let liquidateCount = 0;
    reports.forEach((r: any) => {
      if (r.driftRebalanceRequired) driftCount++;
      if (r.transferRebalanceRequired) transferCount++;
      if (r.liquidateRebalanceRequired) liquidateCount++;
    });
    return {
      totalPortfolios: totalCount,
      driftCount,
      transferCount,
      liquidateCount,
      reports,
    };
  }, [reportsData]);

  const tradeTotals = useMemo(() => {
    let buys = 0;
    let sells = 0;
    let buyCents = 0;
    let sellCents = 0;
    let regSellCents = 0;
    let totalSellCents = 0;
    trades.forEach((t) => {
      const isBuy = t.type === 'BUY' || t.type?.includes('BUY');
      const cents = dollars2cents(t.totalValue);
      if (isBuy) {
        buys++;
        buyCents += cents;
      } else {
        sells++;
        sellCents += cents;
        totalSellCents += cents;
        const acctType = t.account.split(' · ').pop()?.trim() || '';
        if (REGISTERED_TYPES.some((r) => acctType.toUpperCase().includes(r)))
          regSellCents += cents;
      }
    });
    const registeredSellPct =
      totalSellCents > 0 ? (regSellCents / totalSellCents) * 100 : 0;
    return {
      totalBuys: buys,
      totalSells: sells,
      totalBuyCents: buyCents,
      totalSellCents: sellCents,
      registeredSellPct,
    };
  }, [trades]);

  const narrativeLines = useMemo(
    () => buildExecutiveSummary(decisionSummary, tradeTotals),
    [decisionSummary, tradeTotals],
  );

  const handleCancel = async () => {
    setShowCancelConfirm(false);
    try {
      await transitionOptimizer({
        variables: {
          input: { portfolioOptimizerId: operation.id, transition: 'cancel' },
        },
      });
      showToast({ severity: 'success', message: 'Optimizer canceled' });
      onRefresh();
    } catch {
      /* handled by useTradingMutation */
    }
  };

  return (
    <Box>
      {/* Stage stepper */}
      <Box
        display='flex'
        alignItems='center'
        mb={2}
        sx={{
          py: 1,
          px: 1.5,
          borderRadius: 1.5,
          bgcolor: sys.color.surface,
          border: '1px solid',
          borderColor: sys.color.outlineVariant,
        }}
      >
        {OPTIMIZER_STAGE_GROUPS.map((stage, idx) => {
          const isComplete =
            currentStageIdx > idx || (isTerminal && state === 'COMPLETED');
          const isCurrent = currentStageIdx === idx && !isTerminal;
          const isFailed =
            (state === 'FAILED' || state === 'CANCELED') &&
            currentStageIdx === idx;
          const dotColor = isComplete
            ? sys.color.positive
            : isCurrent
              ? sys.color.primary
              : isFailed
                ? sys.color.negative
                : sys.color.disabled;
          return (
            <Box key={stage.label} display='flex' alignItems='center' flex={1}>
              <Box display='flex' alignItems='center' gap={0.5}>
                {isComplete ? (
                  <CheckCircleOutlineIcon
                    sx={{ fontSize: 16, color: dotColor }}
                  />
                ) : (
                  <RadioButtonUncheckedIcon
                    sx={{
                      fontSize: 16,
                      color: dotColor,
                      ...(isCurrent
                        ? {
                            animation: 'pulse 1.5s ease-in-out infinite',
                            '@keyframes pulse': {
                              '0%, 100%': { opacity: 1 },
                              '50%': { opacity: 0.4 },
                            },
                          }
                        : {}),
                    }}
                  />
                )}
                <Typography
                  variant='labelSmall'
                  sx={{
                    ...SMALL_SX,
                    fontWeight: isCurrent ? 700 : 500,
                    color:
                      isComplete || isCurrent
                        ? sys.color.onSurface
                        : sys.color.onDisabled,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {stage.label}
                </Typography>
              </Box>
              {idx < OPTIMIZER_STAGE_GROUPS.length - 1 && (
                <Box
                  flex={1}
                  sx={{
                    height: 2,
                    mx: 0.5,
                    borderRadius: 1,
                    bgcolor: isComplete
                      ? sys.color.positive
                      : sys.color.disabled,
                    transition: 'background 0.3s',
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      {/* Status label */}
      <Box display='flex' alignItems='center' gap={1} mb={1.5}>
        <Typography
          variant='labelSmall'
          sx={{
            ...LABEL_SX,
            px: 1,
            py: 0.25,
            borderRadius: '4px',
            fontWeight: 700,
            bgcolor: isTerminal
              ? state === 'COMPLETED'
                ? sys.color.positiveVariant
                : sys.color.negativeVariant
              : sys.color.primaryVariant,
            color: isTerminal
              ? state === 'COMPLETED'
                ? sys.color.positive
                : sys.color.negative
              : sys.color.primary,
          }}
        >
          {OPTIMIZER_STATE_LABELS[state] || state}
        </Typography>
        {operation.type && (
          <Typography
            variant='labelSmall'
            sx={{ ...SMALL_SX, color: sys.color.onSurfaceVariant }}
          >
            Type: {operation.type}
          </Typography>
        )}
        {operation.numPortfolios != null && (
          <Typography
            variant='labelSmall'
            sx={{ ...SMALL_SX, color: sys.color.onSurfaceVariant }}
          >
            {operation.numPortfolios} portfolios
          </Typography>
        )}
      </Box>

      {/* Executive narrative */}
      {narrativeLines.length > 0 && (
        <Box
          sx={{
            mb: 1.5,
            px: 1.5,
            py: 1,
            borderRadius: 1,
            bgcolor: hexAlpha(sys.color.primary || '#6750A4', 0.04),
            border: '1px solid',
            borderColor: hexAlpha(sys.color.primary || '#6750A4', 0.15),
          }}
        >
          {narrativeLines.map((line, i) => (
            <Typography
              key={i}
              variant='labelSmall'
              sx={{
                ...VALUE_SX,
                lineHeight: 1.5,
                color: sys.color.onSurface,
                display: 'block',
              }}
            >
              {line}
            </Typography>
          ))}
        </Box>
      )}

      {/* Summary cards */}
      {loadingReports ? (
        <Box display='flex' gap={1.5} mb={2}>
          {[0, 1, 2, 3].map((i) => (
            <Box
              key={i}
              flex={1}
              sx={{
                p: 1,
                borderRadius: 1,
                bgcolor: sys.color.surface,
                border: '1px solid',
                borderColor: sys.color.outlineVariant,
              }}
            >
              <Skeleton variant='text' width='60%' />
              <Skeleton variant='text' width='40%' height={24} />
            </Box>
          ))}
        </Box>
      ) : decisionSummary.totalPortfolios > 0 ? (
        <>
          {/* Per-portfolio breakdown */}
          {decisionSummary.reports.length > 0 && (
            <PortfolioBreakdown
              reports={decisionSummary.reports}
              trades={trades}
              loadingTrades={loadingTrades}
              sys={sys}
            />
          )}
        </>
      ) : null}

      {/* Actions */}
      <Box display='flex' gap={1} mt={1}>
        {!isTerminal && (
          <Button
            size='sm'
            variant='outlined'
            label={transitioning ? 'Canceling…' : 'Cancel Optimizer'}
            disabled={transitioning}
            onClick={() => setShowCancelConfirm(true)}
          />
        )}
      </Box>

      <ConfirmationModal
        open={showCancelConfirm}
        title='Cancel Optimizer'
        bodyText='This will stop the optimizer run. Any trades already generated will remain. Continue?'
        confirmButtonLabel='Cancel Optimizer'
        confirmButtonColor={ConfirmButtonColors.destructive}
        confirmButtonVariant={ButtonVariants.filled}
        onConfirm={handleCancel}
        onCancel={() => setShowCancelConfirm(false)}
      />
    </Box>
  );
};

/* ──────────────────────────────── */
/*  Small summary card component   */
/* ──────────────────────────────── */

/* ───────────────────────────────── */
/*  Per-portfolio tax impact strip   */
/* ───────────────────────────────── */

const PortfolioTaxImpact = ({
  insight,
  sys,
}: {
  insight: TaxInsight;
  sys: any;
}) => {
  const hasSells = insight.totalSells > 0;
  if (!hasSells && insight.totalBuys === 0) return null;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        variant='labelSmall'
        sx={{
          ...MICRO_UPPER_SX,
          fontSize: '0.5rem',
          fontWeight: 700,
          letterSpacing: '0.06em',
          color: sys.color.onSurfaceVariant,
          mb: 0.5,
        }}
      >
        TAX & ACCOUNT IMPACT
      </Typography>

      <Box display='flex' gap={1} mb={0.75} flexWrap='wrap'>
        {hasSells && (
          <>
            <MiniMetric
              label='Registered sells'
              value={formatMoney(insight.registeredSellCents)}
              sublabel='No capital gains'
              accent={sys.color.positive}
              bg={sys.color.positiveVariant}
              sys={sys}
            />
            <MiniMetric
              label='Taxable sells'
              value={formatMoney(insight.taxableSellCents)}
              sublabel={
                insight.taxableSellCents > 0 ? 'May trigger gains' : 'None'
              }
              accent={
                insight.taxableSellCents > 0
                  ? sys.color.warning
                  : sys.color.onDisabled
              }
              bg={
                insight.taxableSellCents > 0
                  ? sys.color.warningVariant
                  : sys.color.surface
              }
              sys={sys}
            />
            <MiniMetric
              label='Tax-sheltered %'
              value={`${insight.registeredSellPct.toFixed(0)}%`}
              sublabel='of sell activity'
              accent={sys.color.primary}
              bg={sys.color.neutralHover}
              sys={sys}
            />
          </>
        )}
      </Box>

      {/* Account type chips */}
      <Box display='flex' gap={0.5} flexWrap='wrap'>
        {Object.entries(insight.accountBreakdown)
          .sort(
            ([, a], [, b]) =>
              b.buyCents + b.sellCents - (a.buyCents + a.sellCents),
          )
          .map(([acctType, data]) => {
            const reg = isRegistered(acctType);
            return (
              <Box
                key={acctType}
                sx={{
                  px: 0.75,
                  py: 0.25,
                  borderRadius: '4px',
                  border: '1px solid',
                  borderColor: sys.color.outlineVariant,
                  bgcolor: reg
                    ? hexAlpha(sys.color.positive || '#4CAF50', 0.03)
                    : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Typography
                  variant='labelSmall'
                  sx={{ ...MICRO_SX, fontWeight: 700 }}
                >
                  {acctType || 'Unknown'}
                </Typography>
                {reg && (
                  <Typography
                    variant='labelSmall'
                    sx={{
                      fontSize: '0.45rem',
                      color: sys.color.positive,
                      fontWeight: 600,
                    }}
                  >
                    TAX-FREE
                  </Typography>
                )}
                {data.buys > 0 && (
                  <Typography
                    variant='labelSmall'
                    sx={{ fontSize: '0.5rem', color: sys.color.positive }}
                  >
                    {data.buys}B {formatMoney(data.buyCents, { compact: true })}
                  </Typography>
                )}
                {data.sells > 0 && (
                  <Typography
                    variant='labelSmall'
                    sx={{ fontSize: '0.5rem', color: sys.color.negative }}
                  >
                    {data.sells}S{' '}
                    {formatMoney(data.sellCents, { compact: true })}
                  </Typography>
                )}
              </Box>
            );
          })}
      </Box>
    </Box>
  );
};

const MiniMetric = ({
  label,
  value,
  sublabel,
  accent,
  bg,
  sys,
}: {
  label: string;
  value: string;
  sublabel: string;
  accent: any;
  bg: any;
  sys: any;
}) => (
  <Box
    sx={{
      p: 0.75,
      borderRadius: 1,
      bgcolor: bg,
      borderLeft: '2px solid',
      borderLeftColor: accent,
      minWidth: 120,
    }}
  >
    <Typography
      variant='labelSmall'
      sx={{
        fontSize: '0.45rem',
        letterSpacing: '0.04em',
        color: sys.color.onSurfaceVariant,
      }}
    >
      {label.toUpperCase()}
    </Typography>
    <Typography
      variant='labelSmall'
      sx={{
        fontWeight: 800,
        fontSize: '0.75rem',
        color: accent,
        lineHeight: 1.2,
      }}
    >
      {value}
    </Typography>
    <Typography
      variant='labelSmall'
      sx={{ fontSize: '0.45rem', color: sys.color.onSurfaceVariant }}
    >
      {sublabel}
    </Typography>
  </Box>
);

/* ──────────────────────────────────────────────── */
/*  Allocation diff bar per holding                 */
/* ──────────────────────────────────────────────── */

const AllocationDiffBar = ({ report, sys }: { report: any; sys: any }) => {
  const diffs = useMemo(() => computeHoldingDiffs(report), [report]);
  if (diffs.length === 0) return null;

  const maxPct = Math.max(
    ...diffs.map((d) => Math.max(d.currentPct, d.targetPct)),
    1,
  );

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        variant='labelSmall'
        sx={{
          ...MICRO_UPPER_SX,
          fontSize: '0.5rem',
          fontWeight: 700,
          letterSpacing: '0.06em',
          color: sys.color.onSurfaceVariant,
          mb: 0.75,
        }}
      >
        ALLOCATION CHANGES
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {diffs.map((d) => {
          const isOver = d.deltaPct > 0;
          const barColor = isOver ? sys.color.negative : sys.color.positive;
          const currentW = `${Math.max((d.currentPct / maxPct) * 100, 2)}%`;
          const targetW = `${Math.max((d.targetPct / maxPct) * 100, 2)}%`;
          return (
            <Box key={d.ticker} display='flex' alignItems='center' gap={1}>
              <Typography
                variant='labelSmall'
                sx={{
                  ...SMALL_BOLD_SX,
                  width: 52,
                  textAlign: 'right',
                  flexShrink: 0,
                }}
              >
                {d.ticker}
              </Typography>
              <Box
                flex={1}
                sx={{
                  position: 'relative',
                  height: 14,
                  bgcolor: sys.color.neutralHover,
                  borderRadius: '3px',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: currentW,
                    bgcolor: hexAlpha(barColor || '#888', 0.25),
                    borderRadius: '3px',
                    transition: 'width 0.3s',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: targetW,
                    border: `1.5px dashed ${barColor}`,
                    borderRadius: '3px',
                    boxSizing: 'border-box',
                  }}
                />
              </Box>
              <Typography
                variant='labelSmall'
                sx={{
                  ...MICRO_SX,
                  color: sys.color.onSurfaceVariant,
                  width: 72,
                  textAlign: 'right',
                  flexShrink: 0,
                }}
              >
                {d.currentPct.toFixed(0)}% → {d.targetPct.toFixed(0)}%
              </Typography>
              <Typography
                variant='labelSmall'
                sx={{
                  ...MICRO_SX,
                  fontWeight: 700,
                  color: barColor,
                  width: 16,
                  textAlign: 'center',
                  flexShrink: 0,
                }}
              >
                {isOver ? '▼' : '▲'}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/* ──────────────────────────────────────────────────── */
/*  Per-portfolio breakdown with expandable trade rows  */
/* ──────────────────────────────────────────────────── */

const matchTradesToPortfolio = (
  report: any,
  trades: NormalizedTrade[],
): NormalizedTrade[] => {
  const clientName = getClientName(report);
  if (!clientName) return [];
  return trades.filter((t) => {
    const tradeClient = t.account.split(' · ')[0];
    return tradeClient === clientName;
  });
};

const PortfolioBreakdown = ({
  reports,
  trades,
  loadingTrades,
  sys,
}: {
  reports: any[];
  trades: NormalizedTrade[];
  loadingTrades: boolean;
  sys: any;
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? reports : reports.slice(0, 8);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: sys.color.outlineVariant,
        borderRadius: 1,
        overflow: 'hidden',
        mb: 1.5,
      }}
    >
      <Typography
        variant='labelSmall'
        sx={{
          ...MICRO_UPPER_SX,
          px: 1.5,
          py: 0.75,
          fontWeight: 700,
          letterSpacing: '0.06em',
          color: sys.color.onSurfaceVariant,
          bgcolor: sys.color.neutralHover,
          borderBottom: '1px solid',
          borderColor: sys.color.outlineVariant,
        }}
      >
        PORTFOLIO BREAKDOWN
      </Typography>

      {visible.map((r: any, idx: number) => {
        const isExpanded = expandedId === r.id;
        const portfolioTrades = matchTradesToPortfolio(r, trades);
        const buys = portfolioTrades.filter(
          (t) => t.type === 'BUY' || t.type?.includes('BUY'),
        );
        const sells = portfolioTrades.filter(
          (t) => t.type === 'SELL' || t.type?.includes('SELL'),
        );
        const triggers = getTriggers(r);
        const taxInsight = isExpanded
          ? computeTaxInsight(portfolioTrades)
          : null;

        return (
          <Box key={r.id}>
            <Box
              display='flex'
              alignItems='center'
              gap={1}
              onClick={() => setExpandedId(isExpanded ? null : r.id)}
              sx={{
                px: 1.5,
                py: 0.75,
                cursor: 'pointer',
                bgcolor:
                  idx % 2 === 0 ? sys.color.surface : sys.color.background,
                borderBottom: '1px solid',
                borderColor: sys.color.outlineVariant,
                borderLeft: '3px solid',
                borderLeftColor: isExpanded ? sys.color.primary : 'transparent',
                '&:hover': { bgcolor: sys.color.neutralHover },
                transition: 'all 0.1s',
              }}
            >
              <IconButton
                size='small'
                label='expand'
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedId(isExpanded ? null : r.id);
                }}
              >
                {isExpanded ? (
                  <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
                ) : (
                  <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
              <Box flex={1} minWidth={0}>
                <Box display='flex' alignItems='center' gap={0.75}>
                  <Typography
                    variant='labelSmall'
                    sx={{ ...VALUE_SX, fontWeight: 700 }}
                  >
                    {getPortfolioName(r)}
                  </Typography>
                  <Typography
                    variant='labelSmall'
                    sx={{ ...SMALL_SX, color: sys.color.onSurfaceVariant }}
                  >
                    {getClientName(r)}
                  </Typography>
                </Box>
              </Box>
              <Box display='flex' gap={0.5} alignItems='center' flexShrink={0}>
                {triggers.map((t) => (
                  <TriggerBadge key={t} label={t} sys={sys} />
                ))}
              </Box>
              <Box
                display='flex'
                gap={1.5}
                alignItems='center'
                sx={{ minWidth: 180, justifyContent: 'flex-end' }}
              >
                {buys.length > 0 && (
                  <Typography
                    variant='labelSmall'
                    sx={{ ...SMALL_BOLD_SX, color: sys.color.positive }}
                  >
                    {buys.length} buy{buys.length !== 1 ? 's' : ''}
                  </Typography>
                )}
                {sells.length > 0 && (
                  <Typography
                    variant='labelSmall'
                    sx={{
                      ...SMALL_BOLD_SX,
                      color: sys.color.negative,
                    }}
                  >
                    {sells.length} sell{sells.length !== 1 ? 's' : ''}
                  </Typography>
                )}
                <Typography
                  variant='labelSmall'
                  sx={{ ...SMALL_SX, color: sys.color.onSurfaceVariant }}
                >
                  {formatMoney(r.marketValueCents || 0, { compact: true })}
                </Typography>
              </Box>
            </Box>

            {isExpanded && (
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  bgcolor: sys.color.background,
                  borderBottom: '2px solid',
                  borderColor: sys.color.primary,
                }}
              >
                {/* Why + metrics */}
                <Box display='flex' gap={3} mb={1.5} flexWrap='wrap'>
                  <Box>
                    <Typography
                      variant='labelSmall'
                      sx={{
                        fontSize: '0.5rem',
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                        color: sys.color.onSurfaceVariant,
                        mb: 0.25,
                        textTransform: 'uppercase',
                      }}
                    >
                      WHY OPTIMIZED
                    </Typography>
                    {triggers.map((t) => (
                      <Typography
                        key={t}
                        variant='labelSmall'
                        sx={{
                          ...SMALL_BOLD_SX,
                          color:
                            t === 'Drift'
                              ? sys.color.warning
                              : t === 'Liquidate'
                                ? sys.color.negative
                                : sys.color.primary,
                        }}
                      >
                        {TRIGGER_EXPLANATIONS[t] || t}
                      </Typography>
                    ))}
                  </Box>
                  <Box display='flex' gap={2}>
                    {[
                      {
                        label: 'Market Value',
                        value: formatMoney(r.marketValueCents || 0),
                      },
                      {
                        label: 'Target Cash',
                        value: formatMoney(r.expectedCashCents || 0),
                      },
                      {
                        label: 'Current Cash',
                        value: formatMoney(r.cashCents || 0),
                      },
                      ...(r.modelPortfolio?.translatedName?.en
                        ? [
                            {
                              label: 'Model',
                              value: r.modelPortfolio.translatedName.en,
                            },
                          ]
                        : []),
                    ].map((m) => (
                      <Box key={m.label}>
                        <Typography
                          variant='labelSmall'
                          sx={{
                            fontSize: '0.5rem',
                            color: sys.color.onDisabled,
                            letterSpacing: '0.04em',
                          }}
                        >
                          {m.label.toUpperCase()}
                        </Typography>
                        <Typography variant='labelSmall' sx={LABEL_SX}>
                          {m.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Per-portfolio tax & account impact */}
                {taxInsight && (
                  <PortfolioTaxImpact insight={taxInsight} sys={sys} />
                )}

                {/* Allocation diff visualization */}
                <AllocationDiffBar report={r} sys={sys} />

                {/* Trades */}
                {loadingTrades ? (
                  <Box>
                    {[0, 1, 2].map((i) => (
                      <Skeleton
                        key={i}
                        variant='text'
                        width='80%'
                        sx={{ mb: 0.5 }}
                      />
                    ))}
                  </Box>
                ) : portfolioTrades.length > 0 ? (
                  <Box>
                    <Typography
                      variant='labelSmall'
                      sx={{
                        ...MICRO_UPPER_SX,
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                        color: sys.color.onSurfaceVariant,
                        mb: 0.5,
                      }}
                    >
                      TRADES ({portfolioTrades.length})
                    </Typography>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: sys.color.outlineVariant,
                        borderRadius: 1,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        display='grid'
                        gridTemplateColumns='48px 1fr 70px 70px 90px'
                        gap={0}
                        sx={{
                          px: 1,
                          py: 0.375,
                          bgcolor: sys.color.neutralHover,
                          borderBottom: '1px solid',
                          borderColor: sys.color.outlineVariant,
                        }}
                      >
                        {['SIDE', 'SECURITY', 'QTY', 'PRICE', 'AMOUNT'].map(
                          (h) => (
                            <Typography
                              key={h}
                              variant='labelSmall'
                              sx={{
                                fontSize: '0.5rem',
                                fontWeight: 600,
                                letterSpacing: '0.04em',
                                color: sys.color.onSurfaceVariant,
                              }}
                            >
                              {h}
                            </Typography>
                          ),
                        )}
                      </Box>
                      {portfolioTrades.map((t) => {
                        const isBuy =
                          t.type === 'BUY' || t.type?.includes('BUY');
                        const acctType = parseAccountType(t.account);
                        const reg = isRegistered(acctType);
                        return (
                          <Box
                            key={t.id}
                            display='grid'
                            gridTemplateColumns='48px 1fr 70px 70px 90px'
                            gap={0}
                            sx={{
                              px: 1,
                              py: 0.375,
                              borderBottom: '1px solid',
                              borderColor: sys.color.outlineVariant,
                              '&:last-child': { borderBottom: 'none' },
                              '&:hover': { bgcolor: sys.color.neutralHover },
                            }}
                          >
                            <Typography
                              variant='labelSmall'
                              sx={{
                                ...SMALL_SX,
                                fontWeight: 800,
                                color: tradeSideColor(
                                  sys.color as SysColors,
                                  isBuy,
                                ),
                              }}
                            >
                              {t.type}
                              {t.isSellAll ? ' ALL' : ''}
                            </Typography>
                            <Box>
                              <Box display='flex' alignItems='center' gap={0.5}>
                                <Typography variant='labelSmall' sx={LABEL_SX}>
                                  {t.ticker}
                                </Typography>
                                {acctType && (
                                  <Typography
                                    variant='labelSmall'
                                    sx={{
                                      fontSize: '0.5rem',
                                      color: reg
                                        ? sys.color.positive
                                        : sys.color.onDisabled,
                                      fontWeight: 600,
                                    }}
                                  >
                                    {acctType}
                                    {reg && (
                                      <CheckCircleOutlineIcon
                                        sx={{
                                          fontSize: 10,
                                          ml: 0.4,
                                          verticalAlign: 'middle',
                                          color: 'inherit',
                                        }}
                                      />
                                    )}
                                  </Typography>
                                )}
                              </Box>
                              {t.securityName && (
                                <Typography
                                  variant='labelSmall'
                                  sx={{
                                    fontSize: '0.5rem',
                                    color: sys.color.onDisabled,
                                    ...TRUNCATE_SX,
                                  }}
                                >
                                  {t.securityName}
                                </Typography>
                              )}
                            </Box>
                            <Typography
                              variant='labelSmall'
                              sx={{ ...SMALL_SX, textAlign: 'right' }}
                            >
                              {t.quantity > 0 ? t.quantity.toFixed(2) : '—'}
                            </Typography>
                            <Typography
                              variant='labelSmall'
                              sx={{
                                ...SMALL_SX,
                                color: sys.color.onSurfaceVariant,
                                textAlign: 'right',
                              }}
                            >
                              {t.price > 0 ? `$${t.price.toFixed(2)}` : '—'}
                            </Typography>
                            <Typography
                              variant='labelSmall'
                              sx={{
                                ...LABEL_SX,
                                textAlign: 'right',
                                color: tradeSideColor(
                                  sys.color as SysColors,
                                  isBuy,
                                ),
                              }}
                            >
                              $
                              {t.totalValue.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                ) : (
                  <Typography
                    variant='labelSmall'
                    sx={{
                      ...SMALL_SX,
                      color: sys.color.onDisabled,
                      fontStyle: 'italic',
                    }}
                  >
                    No trade details available yet
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        );
      })}

      {reports.length > 8 && (
        <Box
          sx={{
            px: 1.5,
            py: 0.5,
            cursor: 'pointer',
            textAlign: 'center',
            '&:hover': { bgcolor: sys.color.neutralHover },
          }}
          onClick={() => setShowAll(!showAll)}
        >
          <Typography
            variant='labelSmall'
            sx={{ ...SMALL_BOLD_SX, color: sys.color.primary }}
          >
            {showAll ? 'Show less' : `Show all ${reports.length} portfolios`}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default OptimizerInlineWorkflow;
